import { createFileRoute } from '@tanstack/react-router';
import { FolderKanban, CheckSquare, Clock, Users, TrendingUp } from 'lucide-react';
import { Header, PageContainer } from '@/components/layout';
import { Card, CardBody } from '@/components/ui';
import { useProjects } from '@/hooks/useProjects';
import { useTasks } from '@/hooks/useTasks';
import { useTimeEntries } from '@/hooks/useTimeEntries';
import { useAuthStore } from '@/stores/auth';

export const Route = createFileRoute('/')({
  component: DashboardPage,
});

function DashboardPage() {
  const { user } = useAuthStore();
  const { data: projectsData } = useProjects({ status: 'active', limit: 5 });
  const { data: tasksData } = useTasks({ status: 'in_progress', limit: 5 });
  const { data: timeData } = useTimeEntries({ limit: 10 });

  const activeProjects = projectsData?.data?.length ?? 0;
  const inProgressTasks = tasksData?.data?.length ?? 0;
  const totalHours = timeData?.totals?.totalHours ?? '0';

  return (
    <>
      <Header
        title={`Welcome back, ${user?.firstName ?? 'User'}!`}
        subtitle="Here's what's happening with your projects today."
      />
      <PageContainer>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Active Projects"
            value={activeProjects}
            icon={FolderKanban}
            color="primary"
          />
          <StatCard
            title="In Progress Tasks"
            value={inProgressTasks}
            icon={CheckSquare}
            color="yellow"
          />
          <StatCard title="Hours This Week" value={totalHours} icon={Clock} color="green" />
          <StatCard title="Team Members" value="4" icon={Users} color="purple" />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Projects */}
          <Card>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">Active Projects</h2>
              <a href="/projects" className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                View all
              </a>
            </div>
            <CardBody className="divide-y divide-gray-100 dark:divide-gray-800">
              {projectsData?.data?.map((project: any) => (
                <div key={project.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{project.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {project.client?.name ?? 'Internal Project'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {project._count?.tasks ?? 0} tasks
                      </p>
                    </div>
                  </div>
                </div>
              )) ?? <p className="text-gray-500 dark:text-gray-400 text-sm py-4">No active projects</p>}
            </CardBody>
          </Card>

          {/* My Tasks */}
          <Card>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">Tasks In Progress</h2>
              <a href="/tasks" className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                View all
              </a>
            </div>
            <CardBody className="divide-y divide-gray-100 dark:divide-gray-800">
              {tasksData?.data?.map((task: any) => (
                <div key={task.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">{task.title}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{task.project?.name}</p>
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        task.priority === 'critical'
                          ? 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300'
                          : task.priority === 'high'
                            ? 'bg-yellow-100 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300'
                            : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      {task.priority}
                    </span>
                  </div>
                </div>
              )) ?? <p className="text-gray-500 dark:text-gray-400 text-sm py-4">No tasks in progress</p>}
            </CardBody>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <QuickActionCard
              title="New Project"
              description="Create a new project"
              href="/projects"
              icon={FolderKanban}
            />
            <QuickActionCard
              title="Log Time"
              description="Track your work"
              href="/time"
              icon={Clock}
            />
            <QuickActionCard
              title="Add Task"
              description="Create a new task"
              href="/tasks"
              icon={CheckSquare}
            />
            <QuickActionCard
              title="Ask Aelita"
              description="Get AI insights"
              href="/aelita"
              icon={TrendingUp}
            />
          </div>
        </div>
      </PageContainer>
    </>
  );
}

function StatCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string;
  value: string | number;
  icon: any;
  color: 'primary' | 'green' | 'yellow' | 'purple';
}) {
  const colors = {
    primary: 'bg-primary-100 dark:bg-primary-950 text-primary-600 dark:text-primary-400',
    green: 'bg-green-100 dark:bg-green-950 text-green-600 dark:text-green-400',
    yellow: 'bg-yellow-100 dark:bg-yellow-950 text-yellow-600 dark:text-yellow-400',
    purple: 'bg-purple-100 dark:bg-purple-950 text-purple-600 dark:text-purple-400',
  };

  return (
    <Card>
      <CardBody className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colors[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        </div>
      </CardBody>
    </Card>
  );
}

function QuickActionCard({
  title,
  description,
  href,
  icon: Icon,
}: {
  title: string;
  description: string;
  href: string;
  icon: any;
}) {
  return (
    <a href={href} className="card p-4 hover:shadow-md transition-shadow group">
      <Icon className="h-8 w-8 text-gray-400 group-hover:text-primary-500 transition-colors mb-3" />
      <h3 className="font-medium text-gray-900 dark:text-gray-100">{title}</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
    </a>
  );
}
